import React, { useState, useEffect } from 'react';
import { EyeIcon, EyeOffIcon, RefreshCw } from 'lucide-react';
import { Mail, Lock, UserCircle, Phone, LogIn } from 'lucide-react';
import {
  login as graphqlLogin,
  register as graphqlRegister,
  requestPasswordRecovery,
  type User,
} from '../services/graphql/authService';
import { updateCurrentUserProfile } from '../services/graphql/userService';
import '../styles/shared.css';
import { trackAuthEvent, trackError, trackEvent } from '../services/analytics';
import ProgressDialog from './ProgressDialog';
import { useServiceKey } from '../hooks/useServiceKey';
import CountryDropdown, { CountryCode } from './CountryDropdown';
import { countryCodes } from '../utils/constants';
import Loader from './Loader';
import InputField from './ui/InputField';
import SectionHeader from './ui/SectionHeader';
import Cookies from 'js-cookie';

interface AuthenticationProps {
  currentUser: User | null;
  handleLogout: () => void;
  authLoading: boolean;
  authError: string | null;
  handleProfileUpdate?: (profileData: ProfileUpdateData) => Promise<void>;
}

// Interface for profile update data
interface ProfileUpdateData {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  countryCode: string;
}

// Expanded word list to at least 500 words for password generation
const WORD_LIST = [
  "apple", "river", "mountain", "cloud", "forest", "ocean", "star", "pebble", "sun", "moon",
  "wolf", "falcon", "tree", "stone", "wind", "fire", "leaf", "sky", "rain", "light",
  "shadow", "meadow", "storm", "breeze", "sand", "wave", "echo", "trail", "hill", "valley",
  "fox", "bear", "owl", "hawk", "pine", "cedar", "maple", "birch", "rose", "lily",
  "dawn", "dusk", "ember", "flame", "glow", "mist", "snow", "ice", "frost", "crystal",
  "brook", "creek", "lake", "pond", "reed", "fern", "moss", "root", "twig", "branch",
  "petal", "thorn", "shell", "coral", "reef", "isle", "cove", "cliff", "peak", "ridge",
  "field", "grove", "plain", "steppe", "marsh", "swamp", "delta", "bay", "harbor", "port",
  "sail", "raft", "canoe", "drift", "gale", "gust", "zephyr", "blaze", "spark", "bolt",
  "comet", "nova", "orbit", "aster", "cosmos", "galaxy", "terra", "flora", "fauna", "beacon",
  // 100 so far, now add 400 more
  "acorn", "alder", "algae", "amber", "amethyst", "antler", "apex", "arch", "ash", "aspen",
  "aurora", "avalanche", "badger", "bamboo", "bark", "barn", "basil", "bison", "blossom", "bluff",
  "boar", "boulder", "bramble", "breeze", "buck", "bud", "burrow", "butterfly", "cactus", "calm",
  "canopy", "canyon", "cascade", "cattail", "cave", "cedar", "cherry", "chestnut", "cinder", "clay",
  "clover", "cobble", "coyote", "crag", "crane", "creek", "cricket", "crocus", "crow", "crystal",
  "currant", "daisy", "dell", "dew", "dingo", "dove", "dragonfly", "drizzle", "dune", "eagle",
  "elm", "ember", "fern", "finch", "fir", "flint", "flora", "fog", "foxglove", "frost",
  "fungus", "garnet", "gazelle", "gem", "geyser", "glacier", "glade", "glen", "goose", "granite",
  "grass", "grove", "gull", "hare", "hazel", "heather", "heron", "hickory", "hillock", "holly",
  "honey", "horizon", "hummingbird", "hyacinth", "iceberg", "iris", "ivy", "jackal", "jade", "jay",
  "juniper", "kestrel", "koi", "lagoon", "larch", "lark", "laurel", "lava", "leaf", "lichen",
  "lightning", "lily", "linden", "lion", "lizard", "loch", "loon", "lotus", "lynx", "magpie",
  "mallow", "mammoth", "maple", "marigold", "meadow", "minnow", "mist", "mole", "monarch", "moose",
  "moss", "mount", "mulberry", "musk", "myrtle", "nectar", "newt", "oak", "oasis", "ocelot",
  "onyx", "opal", "orchid", "otter", "owl", "ox", "oyster", "palm", "panther", "parrot",
  "pebble", "pelican", "perch", "petal", "pine", "plover", "plum", "poppy", "porcupine", "prairie",
  "quail", "quartz", "quince", "rabbit", "raccoon", "raven", "reed", "reindeer", "rhododendron", "ridge",
  "river", "robin", "rock", "rose", "rush", "sage", "salmon", "sand", "sapling", "sapphire",
  "scarlet", "sea", "seagull", "seal", "sedge", "shadow", "shale", "sheep", "shell", "shrub",
  "silt", "skylark", "slate", "smoke", "snowdrop", "songbird", "sparrow", "spider", "spruce", "squirrel",
  "starling", "stem", "stone", "stork", "stream", "sunflower", "swallow", "swan", "sycamore", "tamarack",
  "tansy", "tawny", "teal", "thistle", "thrush", "thyme", "tide", "tiger", "toad", "topaz",
  "trillium", "trout", "tulip", "tundra", "turtle", "twig", "valley", "vervain", "vine", "violet",
  "vixen", "vole", "vulture", "walnut", "warbler", "wasp", "waterfall", "weasel", "whale", "whisker",
  "willow", "wind", "wisteria", "wolf", "wolverine", "wood", "wren", "yarrow", "yew", "zebra",
  "zephyr", "acacia", "agate", "albatross", "algae", "alligator", "almond", "aloe", "anchovy", "angel",
  "ant", "apricot", "arbor", "armadillo", "arrow", "artic", "aspen", "aster", "avocet", "azalea",
  "badger", "balsa", "bamboo", "banana", "barberry", "basil", "bass", "bat", "bayberry", "beagle",
  "beaver", "bee", "beetle", "begonia", "bellflower", "bison", "bittern", "blackberry", "blossom", "bluebell",
  "bluebird", "bluejay", "bobcat", "boletus", "borage", "bougainvillea", "bream", "bristlecone", "brittlebush", "buckeye",
  "buffalo", "bulrush", "bumblebee", "burdock", "buttercup", "butternut", "buzzard", "cactus", "caladium", "calf",
  "camellia", "canary", "caper", "capybara", "caracal", "caraway", "cardinal", "caribou", "carnation", "cassia",
  "cat", "catfish", "cedar", "centaury", "chamois", "cherry", "chestnut", "chickadee", "chicory", "chipmunk",
  "cicada", "cinnamon", "civet", "clover", "cloverleaf", "cobweb", "cockle", "cod", "columbine", "coneflower",
  "conifer", "coot", "coral", "cormorant", "cornflower", "corydalis", "cotton", "cottonwood", "cow", "cowbird",
  "coyote", "crab", "crabapple", "crane", "cranefly", "cranberry", "creosote", "crocus", "crow", "cuckoo",
  "currant", "cypress", "dahlia", "daisy", "dandelion", "darter", "date", "deer", "delphinium", "desert",
  "devilfish", "dingo", "dog", "dogwood", "dolphin", "dove", "dragonfly", "drake", "duck", "duckweed",
  "dune", "eagle", "earwig", "echidna", "edelweiss", "eel", "egret", "elder", "elm", "emu",
  "ermine", "escarpment", "evergreen", "falcon", "fawn", "feather", "fen", "fern", "fig", "fir",
  "firefly", "firn", "fish", "flamingo", "flax", "flicker", "flora", "fly", "foal", "fog",
  "fox", "foxglove", "frond", "fungus", "gannet", "gardenia", "garlic", "gazelle", "gecko", "geranium",
  "gibbon", "ginkgo", "giraffe", "gladiolus", "glider", "goat", "goldfinch", "goose", "gopher", "goshawk",
  "gourd", "grackle", "grape", "grasshopper", "grebe", "grevillea", "grouse", "guava", "gull", "hare",
  "hawk", "hazel", "heather", "hedgehog", "heron", "hibiscus", "hickory", "hippo", "holly", "honeybee",
  "hornet", "horse", "hummingbird", "hyacinth", "hyena", "ibex", "ibis", "ice", "iguana", "impala",
  "indigo", "iris", "ivy", "jackal", "jackdaw", "jaguar", "jasmine", "jay", "jellyfish", "jerboa",
  "jonquil", "junco", "juniper", "kale", "kangaroo", "kapok", "kestrel", "kingfisher", "kiwi", "koala",
  "koi", "kookaburra", "krill", "lamb", "larch", "lark", "laurel", "lavender", "leopard", "lichen",
  "lily", "lion", "lizard", "lobelia", "locust", "loon", "lotus", "lupine", "lynx", "macaw",
  "magnolia", "mallow", "mammoth", "manatee", "maple", "marigold", "marten", "meadowlark", "mint", "mole",
  "monarch", "mongoose", "monkey", "moorhen", "moose", "morningglory", "moss", "mountain", "mulberry", "musk",
  "myrtle", "narwhal", "nectar", "newt", "nightingale", "nutmeg", "oak", "ocelot", "octopus", "olive",
  "onyx", "opal", "orchid", "oriole", "osprey", "otter", "owl", "ox", "oyster", "palm",
  "panther", "papaya", "parakeet", "parrot", "partridge", "peach", "peacock", "peafowl", "pebble", "pelican",
  "penguin", "perch", "petal", "petunia", "pheasant", "phoenix", "pine", "pintail", "plover", "plum",
  "poinsettia", "polar", "poppy", "porcupine", "prairie", "pronghorn", "ptarmigan", "puffin", "puma", "quail",
  "quartz", "quince", "rabbit", "raccoon", "raven", "reed", "reindeer", "rhododendron", "ridge", "robin",
  "rock", "rose", "rush", "sage", "salmon", "sand", "sapling", "sapphire", "scarlet", "sea",
  "seagull", "seal", "sedum", "shadow", "shale", "sheep", "shell", "shrub", "silt", "skylark",
  "slate", "smoke", "snapdragon", "snow", "snowdrop", "songbird", "sparrow", "spider", "spruce", "squirrel",
  "starling", "stem", "stone", "stork", "stream", "sunflower", "swallow", "swan", "sycamore", "tamarack",
  "tansy", "tawny", "teal", "thistle", "thrush", "thyme", "tide", "tiger", "toad", "topaz",
  "trillium", "trout", "tulip", "tundra", "turtle", "twig", "valley", "vervain", "vine", "violet",
  "vixen", "vole", "vulture", "walnut", "warbler", "wasp", "waterfall", "weasel", "whale", "whisker",
  "willow", "wind", "wisteria", "wolf", "wolverine", "wood", "wren", "yarrow", "yew", "zebra",
  "zephyr"
];

/**
 * Generates a random password consisting of three words separated by dashes.
 * @returns {string} The generated password.
 */
function generateThreeWordPassword(): string {
  const getRandomWord = () => WORD_LIST[Math.floor(Math.random() * WORD_LIST.length)];
  return `${getRandomWord()}-${getRandomWord()}-${getRandomWord()}`;
}

const Authentication: React.FC<AuthenticationProps> = ({
  currentUser,
  handleLogout,
  authLoading = false,
  authError = null,
  handleProfileUpdate
}) => {
  const [authMode, setAuthMode] = useState<'login' | 'register' | 'recovery' | 'profile'>('login');
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  
  // Enhanced registration fields
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regConfirmPassword, setRegConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>(countryCodes[0]);
  const [showRegPassword, setShowRegPassword] = useState(true); // Default to visible for generated password
  const [localAuthError, setLocalAuthError] = useState<string | null>(null);
  const [localAuthLoading, setLocalAuthLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  
  // Field-specific error tracking
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  
  // Recovery state
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [recoveryMessage, setRecoveryMessage] = useState<string | null>(null);
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const { serviceKey } = useServiceKey();

  // Section collapsed state - collapsed by default, can be expanded via header link
  const [isCollapsed, setIsCollapsed] = useState(true);

  // Listen for event from header to expand the login panel
  useEffect(() => {
    const handleShowLoginPanel = () => {
      setIsCollapsed(false);
      // Scroll to the auth container
      const authContainer = document.querySelector('[data-testid="auth-container"]');
      if (authContainer) {
        authContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };

    window.addEventListener('showLoginPanel', handleShowLoginPanel);
    return () => {
      window.removeEventListener('showLoginPanel', handleShowLoginPanel);
    };
  }, []);

  // Profile editing state
  const [profileFirstName, setProfileFirstName] = useState('');
  const [profileLastName, setProfileLastName] = useState('');
  const [profilePhone, setProfilePhone] = useState('');
  const [profileCountry, setProfileCountry] = useState<CountryCode>(countryCodes[0]);
  const [profileUpdateLoading, setProfileUpdateLoading] = useState(false);
  const [profileUpdateSuccess, setProfileUpdateSuccess] = useState(false);
  const [profileFieldErrors, setProfileFieldErrors] = useState<Record<string, string>>({});

  // Function to generate and set password
  const handleGeneratePassword = () => {
    const newPassword = generateThreeWordPassword();
    setRegPassword(newPassword);
    setRegConfirmPassword(newPassword); // Also set confirm password for convenience
    setShowRegPassword(true); // Ensure generated password is visible
  };

  // Clear any local auth errors when the auth mode changes
  useEffect(() => {
    setLocalAuthError(null);
  }, [authMode]);

  const handleCountrySelect = (country: CountryCode) => {
    setSelectedCountry(country);
  };

  // Generate a password by default on mount for registration
  useEffect(() => {
    if (authMode === 'register') {
      handleGeneratePassword(); // Generate and set password
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authMode]);

  // On mount, pre-fill login email from cookie if present
  useEffect(() => {
    const savedEmail = Cookies.get('rememberedLoginEmail');
    if (savedEmail) {
      setLoginEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  // Load user profile data when user is logged in and enters edit profile mode
  useEffect(() => {
    if (currentUser && authMode === 'profile') {
      // Pre-fill form with user's data (GraphQL User format)
      const nameParts = (currentUser.name || '').split(' ');
      setProfileFirstName(nameParts[0] || '');
      setProfileLastName(nameParts.slice(1).join(' ') || '');

      // Parse phone number to extract country code
      const fullPhone = currentUser.phone || '';
      let matchedCountry = countryCodes[0]; // Default
      let phoneWithoutCode = fullPhone;

      // Sort country codes by length (longest first) to match correctly
      // e.g., +1684 should match before +1
      const sortedCountryCodes = [...countryCodes].sort(
        (a, b) => b.code.length - a.code.length
      );

      for (const country of sortedCountryCodes) {
        if (fullPhone.startsWith(country.code)) {
          matchedCountry = country;
          phoneWithoutCode = fullPhone.slice(country.code.length);
          break;
        }
      }

      setProfilePhone(phoneWithoutCode);
      setProfileCountry(matchedCountry);

      // Reset states
      setProfileUpdateSuccess(false);
      setProfileFieldErrors({});
      setLocalAuthError(null);
    }
  }, [currentUser, authMode]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalAuthLoading(true);
    setLocalAuthError(null);

    // Remember Me logic
    if (rememberMe && loginEmail) {
      Cookies.set('rememberedLoginEmail', loginEmail, { expires: 30 });
    } else {
      Cookies.remove('rememberedLoginEmail');
    }

    try {
      // Track login attempt
      trackEvent('Authentication', 'Login Attempt');

      // GraphQL login using email and password
      const { user, error } = await graphqlLogin(loginEmail, loginPassword);

      if (error || !user) {
        throw new Error(error?.message || 'Login failed. Please check your credentials.');
      }

      console.log('Login successful:', user);
      // Track successful login
      trackAuthEvent('Login Success', 'email', user.$id);
      // Clear form
      setLoginEmail('');
      setLoginPassword('');
      // Trigger page reload to refresh auth state
      window.location.reload();
    } catch (err: unknown) {
      console.error('Login error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Login failed. Please check your credentials.';
      setLocalAuthError(errorMessage);
      // Track login error
      trackError(`Login failed: ${errorMessage}`, false);
    } finally {
      setLocalAuthLoading(false);
    }
  };    const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear previous field errors
    setFieldErrors({});
    setLocalAuthError(null);
    
    const errors: Record<string, string> = {};
    
    // Validate all required fields
    if (!firstName.trim()) errors.firstName = 'First name is required';
    if (!lastName.trim()) errors.lastName = 'Last name is required';
    if (!regEmail.trim()) errors.regEmail = 'Email is required';
    if (!regPassword.trim()) errors.regPassword = 'Password is required';
    if (!regConfirmPassword.trim()) errors.regConfirmPassword = 'Confirm password is required';
    if (!phoneNumber.trim()) errors.phoneNumber = 'Phone number is required';
    else {
      // Validate phone number to be numeric, spaces allowed
      const phoneRegex = /^[0-9 ]+$/;
      if (!phoneRegex.test(phoneNumber)) {
        errors.phoneNumber = 'Phone number must contain only numbers and spaces';
      }
    }

    // Validate password match
    if (regPassword && regConfirmPassword && regPassword !== regConfirmPassword) {
      errors.regConfirmPassword = 'Passwords do not match';
    }

    // Validate email format
    if (regEmail.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(regEmail)) {
        errors.regEmail = 'Please enter a valid email address';
      }
    }

    // If there are validation errors, set them and return
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      trackError('Registration failed: Validation errors', false);
      return;
    }

    setLocalAuthLoading(true);
    setLocalAuthError(null);

    try {
      // Track registration attempt
      trackEvent('Authentication', 'Registration Attempt');

      // Create user with GraphQL API
      const fullName = `${firstName} ${lastName}`;
      const fullPhone = `${selectedCountry.code}${phoneNumber}`;
      const { user, error } = await graphqlRegister(regEmail, regPassword, fullName, fullPhone);

      if (error || !user) {
        throw new Error(error?.message || 'Registration failed. Please try again.');
      }

      console.log('Registration successful:', user);
      trackAuthEvent('Registration Success', 'email', user.$id);

      // Clear form
      setRegEmail('');
      setRegPassword('');
      setRegConfirmPassword('');
      setFirstName('');
      setLastName('');
      setPhoneNumber('');
      setSelectedCountry(countryCodes[0]);

      // Reload to refresh auth state
      window.location.reload();
    } catch (err: unknown) {
      console.error('Registration error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Registration failed. Please try again.';
      setLocalAuthError(errorMessage);
      trackError(`Registration failed: ${errorMessage}`, false);
    } finally {
      setLocalAuthLoading(false);
    }
  };
  /**
   * Handles password recovery by sending a reset email via GraphQL API.
   * @param e React form event
   */
  const handleRecovery = async (e: React.FormEvent) => {
    e.preventDefault();
    setRecoveryLoading(true);
    setRecoveryMessage(null);
    // Track recovery attempt
    trackEvent('Authentication', 'Password Recovery Attempt');
    try {
      console.log('[Recovery] Requesting password recovery for:', recoveryEmail);

      // GraphQL password recovery
      const { success, error } = await requestPasswordRecovery(recoveryEmail);

      if (error) {
        // Still show a generic message for security
        setRecoveryMessage('If this email exists, a password reset link has been sent.');
        console.warn('[Recovery] Recovery not yet implemented:', error.message);
      } else if (success) {
        setRecoveryMessage('If this email exists, a password reset link has been sent.');
        trackAuthEvent('Password Recovery Email Sent', 'email');
        console.log('[Recovery] Password reset email sent successfully.');
      } else {
        setRecoveryMessage('Password recovery is not yet available. Please contact support.');
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Recovery failed. Please try again.';
      setRecoveryMessage(errorMessage);
      trackError(`Password recovery failed: ${errorMessage}`, false);
      console.error('[Recovery] Error:', err);
    } finally {
      setRecoveryLoading(false);
    }
  };

  // Handle profile update
  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear previous field errors
    setProfileFieldErrors({});
    setLocalAuthError(null);
    setProfileUpdateSuccess(false);
    
    const errors: Record<string, string> = {};
    
    // Validate all required fields
    if (!profileFirstName.trim()) errors.profileFirstName = 'First name is required';
    if (!profileLastName.trim()) errors.profileLastName = 'Last name is required';
    if (!profilePhone.trim()) errors.profilePhone = 'Phone number is required';
    else {
      // Validate phone number to be numeric, spaces allowed
      const phoneRegex = /^[0-9 ]+$/;
      if (!phoneRegex.test(profilePhone)) {
        errors.profilePhone = 'Phone number must contain only numbers and spaces';
      }
    }

    // If there are validation errors, set them and return
    if (Object.keys(errors).length > 0) {
      setProfileFieldErrors(errors);
      trackError('Profile update failed: Validation errors', false);
      return;
    }    setProfileUpdateLoading(true);
    setLocalAuthError(null);

    try {
      // Track profile update attempt
      trackEvent('User', 'Profile Update Attempt');

      // Call GraphQL API to update profile
      const { success, error } = await updateCurrentUserProfile({
        firstName: profileFirstName.trim(),
        lastName: profileLastName.trim(),
        phoneNumber: profilePhone.trim(),
        countryCode: profileCountry.code,
      });

      if (error) {
        setLocalAuthError(`Profile update failed: ${error.message}`);
        trackError(`Profile update failed: ${error.message}`, false);
        return;
      }

      if (success) {
        trackEvent('User', 'Profile Update Success');
        // Show success message briefly
        setLocalAuthError(null);
        alert('Profile updated successfully!');
      }
    } catch (err) {
      console.error('Profile update exception:', err);
      setLocalAuthError(`Profile update failed: ${err instanceof Error ? err.message : String(err)}`);
      trackError(`Profile update exception: ${err instanceof Error ? err.message : String(err)}`, false);
    } finally {
      setProfileUpdateLoading(false);
    }
  };

  // Track logout events
  const handleLogoutWithTracking = () => {
    trackEvent('Authentication', 'Logout Attempt');
    handleLogout();
    // Only clear login email if Remember Me is not checked
    if (!rememberMe) {
      setLoginEmail('');
    } else {
      // Restore email from cookie to ensure it stays visible after logout
      const savedEmail = Cookies.get('rememberedLoginEmail');
      if (savedEmail) {
        setLoginEmail(savedEmail);
      }
    }
    setLoginPassword(''); // Always clear password
  };

  // Use component's local state or props
  const displayError = localAuthError || authError;
  const isLoading = localAuthLoading || authLoading;
  if (isLoading) {
    return (
      <ProgressDialog isOpen={isLoading} message="Loading authentication..." subMessage="Please wait while we check your credentials." />
    );
  }  return (
    <div className="card-container" data-testid="auth-container">
      <SectionHeader
        title={currentUser ? "Account" : "Login / Register"}
        icon={LogIn}
        collapsible={true}
        collapsed={isCollapsed}
        onCollapsedChange={setIsCollapsed}
      >
        {/* Current user info */}
        {currentUser ? (
        <div>
          <div className="auth-tab-container" data-testid="auth-tab-container">
            <button
              className={`auth-tab ${authMode === 'login' ? 'auth-tab-active' : ''}`}
              onClick={() => setAuthMode('login')}
              data-testid="auth-tab-user-info"
            >
              User Info
            </button>
            <button
              className={`auth-tab ${authMode === 'profile' ? 'auth-tab-active' : ''}`}
              onClick={() => setAuthMode('profile')}
              data-testid="auth-tab-profile"
            >
              Edit Profile
            </button>
          </div>
            {authMode === 'profile' ? (
            <form onSubmit={handleProfileSubmit} className="auth-form" data-testid="auth-profile-form">
              {/* First Name and Last Name on the same row for desktop */}
              <div className="md:flex md:space-x-2">
                {/* First Name */}
                <div className="form-group md:w-1/2">
                  <label className="form-label">First Name *</label>
                  <InputField
                    type="text"
                    value={profileFirstName}
                    onChange={e => setProfileFirstName(e.target.value)}
                    inputClassName="form-input form-input-with-icon-left"
                    placeholder="First Name"
                    required
                    error={profileFieldErrors.profileFirstName}
                    iconLeft={<UserCircle className="form-input-icon-left" />}
                    data-testid="auth-profile-first-name-input"
                  />
                </div>

                {/* Last Name */}
                <div className="form-group md:w-1/2">
                  <label className="form-label">Last Name *</label>
                  <InputField
                    type="text"
                    value={profileLastName}
                    onChange={e => setProfileLastName(e.target.value)}
                    inputClassName="form-input form-input-with-icon-left"
                    placeholder="Last Name"
                    required
                    error={profileFieldErrors.profileLastName}
                    iconLeft={<UserCircle className="form-input-icon-left" />}
                    data-testid="auth-profile-last-name-input"
                  />
                </div>
              </div>

              {/* Phone Number with Country Code */}
              <div className="form-group">
                <label className="form-label">Phone Number *</label>
                <div className="flex items-start space-x-2">
                  <CountryDropdown
                    selectedCountry={profileCountry}
                    onSelect={setProfileCountry}
                    disabled={false}
                    data-testid="auth-profile-country-dropdown"
                  />
                  <div className="flex-1">
                    <InputField
                      type="tel"
                      name="phone"
                      autoComplete="tel"
                      value={profilePhone}
                      onChange={(e) => setProfilePhone(e.target.value)}
                      inputClassName="form-input form-input-with-icon-left w-full"
                      placeholder="Phone Number"
                      required
                      error={profileFieldErrors.profilePhone}
                      iconLeft={<Phone className="form-input-icon-left" />}
                      data-testid="auth-profile-phone-input"
                    />
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Email Address:</label>
                <InputField
                  type="email"
                  value={currentUser.email || ''}
                  inputClassName="form-input form-input-with-icon-left"
                  placeholder="Email Address"
                  disabled={true}
                  iconLeft={<Mail className="form-input-icon-left" />}
                  data-testid="auth-profile-email-display"
                />
                <p className="text-sm text-gray-500 mt-1">Email address cannot be changed. Contact support if needed.</p>
              </div>

              <button
                type="submit"
                disabled={profileUpdateLoading}
                className="auth-button-success"
                data-testid="auth-profile-submit-button"
              >
                {profileUpdateLoading ? (
                  <>
                    <Loader className="auth-spinner" />
                    Updating Profile...
                  </>
                ) : (
                  'Update Profile'
                )}
              </button>

              {profileUpdateSuccess && (
                <div className="auth-message auth-message-success mt-4" data-testid="auth-profile-success-message">
                  Profile updated successfully!
                </div>
              )}

              {localAuthError && (
                <div className="auth-error-message mt-4" data-testid="auth-profile-error-message">
                  {localAuthError}
                </div>
              )}
            </form>
          ) : (
            <div className="auth-user-info" data-testid="auth-user-info-section">
              <div className="auth-user-header">
                <h3 className="auth-user-title" data-testid="auth-user-info-header">Logged In User</h3>
                <span className="auth-user-badge" data-testid="auth-user-session-badge">Active Session</span>
              </div>
              <div className="auth-user-details">
                <p className="auth-user-detail">
                  <span className="auth-user-label">Email:</span>
                  <span className="auth-user-value" data-testid="auth-user-email">
                    {currentUser.email}
                    {currentUser.labels?.includes('super_admin') && (
                      <span className="ml-2 px-2 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded-full dark:bg-purple-900 dark:text-purple-300" data-testid="auth-user-superadmin-badge">
                        Superadmin
                      </span>
                    )}
                    {currentUser.labels?.includes('service_provider_admin') && (
                      <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full dark:bg-blue-900 dark:text-blue-300" data-testid="auth-user-service-provider-badge">
                        Service Provider Admin
                      </span>
                    )}
                  </span>
                </p>
                <p className="auth-user-detail">
                  <span className="auth-user-label">Email verified:</span>
                  {currentUser.emailVerification ? (
                    <span className="auth-user-verified" data-testid="auth-user-verified-yes">Yes</span>
                  ) : (
                    <span className="auth-user-not-verified" data-testid="auth-user-verified-no">No</span>
                  )}
                </p>
                <p className="auth-user-detail">
                  <span className="auth-user-label">Name:</span>
                  <span className="auth-user-value" data-testid="auth-user-name">{currentUser.name || 'N/A'}</span>
                </p>
              </div>
              <button
                onClick={handleLogoutWithTracking}
                disabled={isLoading}
                className="auth-button-danger"
                data-testid="auth-logout-button"
              >
                {isLoading ? (
                  <>
                    <Loader className="auth-spinner" />
                    Logging out...
                  </>
                ) : (
                  'Logout'
                )}
              </button>
            </div>
          )}
        </div>
      ) : (
        <div>
          <div className="auth-tab-container" data-testid="auth-guest-tab-container">
            <button
              className={`auth-tab ${authMode === 'login' ? 'auth-tab-active' : ''}`}
              onClick={() => setAuthMode('login')}
              data-testid="auth-tab-login"
            >
              Login
            </button>
            <button
              className={`auth-tab ${authMode === 'register' ? 'auth-tab-active' : ''}`}
              onClick={() => setAuthMode('register')}
              data-testid="auth-tab-register"
            >
              Register
            </button>
            <button
              className={`auth-tab ${authMode === 'recovery' ? 'auth-tab-active' : ''}`}
              onClick={() => setAuthMode('recovery')}
              data-testid="auth-tab-recovery"
            >
              Recovery
            </button>
          </div>          {authMode === 'login' ? (
            <form onSubmit={handleLogin} className="auth-form" data-testid="auth-login-form">
              <div className="form-group">
                <label className="form-label">Email:</label>
                <InputField
                  type="email"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  inputClassName="form-input form-input-with-icon-left"
                  placeholder="user@example.com"
                  required
                  autoComplete="username"
                  iconLeft={<Mail className="form-input-icon-left" />}
                  data-testid="auth-login-email-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Password:</label>
                <div className="auth-password-container form-input-container relative">
                  <InputField
                    type={showLoginPassword ? "text" : "password"}
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    inputClassName="form-input form-input-with-icon-left pr-10"
                    placeholder="••••••••"
                    required
                    autoComplete="current-password"
                    iconLeft={<Lock className="form-input-icon-left" />}
                    data-testid="auth-login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowLoginPassword(!showLoginPassword)}
                    className="auth-password-toggle absolute inset-y-0 right-0 pr-3 flex items-center"
                    data-testid="auth-login-password-toggle"
                  >
                    {showLoginPassword ? (
                      <EyeOffIcon className="auth-password-toggle-icon" />
                    ) : (
                      <EyeIcon className="auth-password-toggle-icon" />
                    )}
                  </button>
                </div>
              </div>              <div className="form-group">
                <div className="flex items-center mb-2">
                  <input
                    type="checkbox"
                    id="rememberMe"
                    checked={rememberMe}
                    onChange={() => {
                      const newValue = !rememberMe;
                      setRememberMe(newValue);
                      if (!newValue) {
                        Cookies.remove('rememberedLoginEmail');
                        setLoginEmail('');
                      }
                    }}
                    className="mr-2"
                    data-testid="auth-login-remember-me-checkbox"
                  />
                  <label htmlFor="rememberMe" className="text-sm select-none cursor-pointer">Remember Me</label>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="auth-button-primary"
                data-testid="auth-login-submit-button"
              >
                {isLoading ? (
                  <>
                    <Loader className="auth-spinner" />
                    Logging in...
                  </>
                ) : (
                  'Login'
                )}
              </button>
            </form>          ) : authMode === 'register' ? (            <form onSubmit={handleRegister} className="auth-form" data-testid="auth-register-form">              {/* First Name and Last Name on the same row for desktop */}
              <div className="md:flex md:space-x-2">
                {/* First Name */}
                <div className="form-group md:w-1/2">
                  <label className="form-label">First Name *</label>                  <InputField
                    type="text"
                    value={firstName}
                    onChange={e => setFirstName(e.target.value)}
                    inputClassName="form-input form-input-with-icon-left"
                    placeholder="First Name"
                    required
                    error={fieldErrors.firstName}
                    iconLeft={<UserCircle className="form-input-icon-left" />}
                    data-testid="auth-register-first-name-input"
                  />
                </div>

                {/* Last Name */}
                <div className="form-group md:w-1/2">
                  <label className="form-label">Last Name *</label>                  <InputField
                    type="text"
                    value={lastName}
                    onChange={e => setLastName(e.target.value)}
                    inputClassName="form-input form-input-with-icon-left"
                    placeholder="Last Name"
                    required
                    error={fieldErrors.lastName}
                    iconLeft={<UserCircle className="form-input-icon-left" />}
                    data-testid="auth-register-last-name-input"
                  />
                </div>
              </div>

              {/* Phone Number with Country Code */}
              <div className="form-group">
                <label className="form-label">Phone Number *</label>
                <div className="flex items-start space-x-2">
                  <CountryDropdown
                    selectedCountry={selectedCountry}
                    onSelect={handleCountrySelect}
                    disabled={false}
                    data-testid="auth-register-country-dropdown"
                  />
                  <div className="flex-1">
                    <InputField
                      type="tel"
                      name="phone"
                      autoComplete="tel"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      inputClassName="form-input form-input-with-icon-left w-full"
                      placeholder="Phone Number"
                      required
                      error={fieldErrors.phoneNumber}
                      iconLeft={<Phone className="form-input-icon-left" />}
                      data-testid="auth-register-phone-input"
                    />
                  </div>
                </div>
              </div>              {/* Email */}
              <div className="form-group">
                <label className="form-label">Email Address *</label>
                <InputField
                  type="email"
                  value={regEmail}
                  onChange={(e) => setRegEmail(e.target.value)}
                  inputClassName="form-input form-input-with-icon-left"
                  placeholder="Email Address"
                  required
                  error={fieldErrors.regEmail}
                  autoComplete="username"
                  iconLeft={<Mail className="form-input-icon-left" />}
                  data-testid="auth-register-email-input"
                />
              </div>

              {/* Password and Confirm Password on the same row for desktop */}
              <div className="md:flex md:space-x-2">
                {/* Password */}
                <div className="form-group md:w-1/2">
                  <label className="form-label">Password *</label>
                  <div className="form-input-container relative">                    <InputField
                      type={showRegPassword ? "text" : "password"}
                      value={regPassword}
                      onChange={(e) => setRegPassword(e.target.value)}
                      inputClassName="form-input form-input-with-icon-left pr-20"
                      placeholder="Password"
                      required
                      error={fieldErrors.regPassword}
                      autoComplete="new-password"
                      iconLeft={<Lock className="form-input-icon-left" />}
                      data-testid="auth-register-password-input"
                    />
                    <div className="absolute inset-y-0 right-0 pr-2 flex items-center space-x-1">                      <button
                        type="button"
                        onClick={handleGeneratePassword}
                        className="p-1 text-foreground-muted hover:text-foreground"
                        aria-label="Generate new password"
                        data-testid="auth-register-password-generate-button"
                      >
                        <RefreshCw size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowRegPassword(!showRegPassword)}
                        className="p-1 text-foreground-muted hover:text-foreground"
                        aria-label={showRegPassword ? "Hide password" : "Show password"}
                        data-testid="auth-register-password-toggle"
                      >
                        {showRegPassword ? <EyeOffIcon size={16} /> : <EyeIcon size={16} />}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Confirm Password */}
                <div className="form-group md:w-1/2">
                  <label className="form-label">Confirm Password *</label>                  <InputField
                    type="password"
                    value={regConfirmPassword}
                    onChange={(e) => setRegConfirmPassword(e.target.value)}
                    inputClassName="form-input form-input-with-icon-left"
                    placeholder="Confirm Password"
                    required
                    error={fieldErrors.regConfirmPassword}
                    autoComplete="new-password"
                    iconLeft={<Lock className="form-input-icon-left" />}
                    data-testid="auth-register-confirm-password-input"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={localAuthLoading}
                className="auth-button-success"
                data-testid="auth-register-submit-button"
              >
                {localAuthLoading ? (
                  <>
                    <Loader className="auth-spinner" />
                    Registering...
                  </>
                ) : (
                  'Register'
                )}
              </button>
            </form>          ) : (
            // Recovery form
            <form onSubmit={handleRecovery} className="auth-form" data-testid="auth-recovery-form">
              <div className="form-group">
                <label className="form-label">Email for password recovery:</label>
                <InputField
                  type="email"
                  value={recoveryEmail}
                  onChange={(e) => setRecoveryEmail(e.target.value)}
                  inputClassName="form-input form-input-with-icon-left"
                  placeholder="user@example.com"
                  required
                  autoComplete="username"
                  iconLeft={<Mail className="form-input-icon-left" />}
                  data-testid="auth-recovery-email-input"
                />
              </div>
              <button
                type="submit"
                disabled={recoveryLoading}
                className="auth-button-warning"
                data-testid="auth-recovery-submit-button"
              >
                {recoveryLoading ? (
                  <>
                    <Loader className="auth-spinner" />
                    Sending recovery email...
                  </>
                ) : (
                  'Send Recovery Email'
                )}
              </button>
              {recoveryMessage && (
                <div className={`auth-message ${recoveryMessage.includes('password reset link') ? 'auth-message-success' : 'auth-message-error'}`} data-testid="auth-recovery-message">
                  {recoveryMessage}
                </div>
              )}
            </form>
          )}

          {(localAuthError || authError) && authMode !== 'recovery' && (
            <div className="auth-error-message" data-testid="auth-error-message">
              {localAuthError || authError}
            </div>
          )}
        </div>
        )}
      </SectionHeader>
    </div>
  );
};

export default Authentication;
